import { useEffect, useRef, useState } from "react";
import { TextInput, StyleSheet } from "react-native";

interface GrowingTextInputProps {
  value: string;
  setValue: (value: string) => void;
  placeholder?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeySubmit?: () => void;
  minHeight?: number;
  maxHeight?: number;
}

const GrowingTextInput = ({
  value,
  setValue,
  placeholder,
  onKeySubmit,
  onBlur,
  onFocus,
  minHeight = 36,
  maxHeight = 140,
}: GrowingTextInputProps) => {
  const [height, setHeight] = useState(minHeight);
  const ignoreNextNewline = useRef(false);
  const skipNextContentSizeChange = useRef(false);

  // Reset height when text cleared
  useEffect(() => {
    if (value === "") setHeight(minHeight);
  }, [value, minHeight]);

  const handleKeyPress = (e: any) => {
    const key = e.nativeEvent.key;

    if (key === "Enter" || key === "\n") {
      if (e.nativeEvent.shiftKey) {
        // allow newline
        return;
      }

      // Enter without Shift → send message
      skipNextContentSizeChange.current = true;
      ignoreNextNewline.current = true;
      onKeySubmit?.();
    }
  };

  const handleChangeText = (text: string) => {
    // Remove the newline RNW inserts when Enter is pressed
    if (ignoreNextNewline.current) {
      ignoreNextNewline.current = false;

      return;
    }

    setValue(text);
  };

  const onContentSizeChange = (event: any) => {
    if (skipNextContentSizeChange.current) {
      skipNextContentSizeChange.current = false;
      return;
    }

    const contentHeight = event.nativeEvent.contentSize?.height ?? minHeight;

    const newHeight = Math.min(maxHeight, Math.max(minHeight, contentHeight));

    setHeight(newHeight);
  };

  return (
    <TextInput
      multiline
      value={value}
      onChangeText={handleChangeText}
      onContentSizeChange={onContentSizeChange}
      onKeyPress={handleKeyPress}
      onFocus={onFocus}
      onBlur={onBlur}
      style={[
        styles.input,
        {
          height,
          maxHeight,
        },
      ]}
      placeholder={placeholder}
      placeholderTextColor="#667085"
      scrollEnabled={height >= maxHeight}
    />
  );
};

const styles = StyleSheet.create({
  input: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderColor: "transparent",
    outlineColor: "transparent",
  },
});

export default GrowingTextInput;
